/**
 * Coordinate search: parsing what people actually paste to each other in the field.
 *
 * Latitude first is the primary reading — that is the order coordinates are dictated in.
 * Hemisphere letters, when present, override the order, so `E31.313 N50.286` still lands
 * in the right place.
 *
 * DMS needs its markers (`°`, `'`, `"`). Bare `50 17 10` is deliberately NOT read as
 * degrees-minutes-seconds: `55 37` is a perfectly ordinary decimal pair for Moscow, and a
 * parser that guesses between the two readings flies the map somewhere the operator did
 * not ask for. Anything ambiguous is refused with a reason instead.
 *
 * Nothing here is a geocoder: no network, no place names. Coordinates work offline.
 */

import { T } from "../strings";

export interface Point {
  lat: number;
  lon: number;
}

export type CoordParse = { ok: true; point: Point } | { ok: false; error: string };

const LAT_MAX = 90;
const LON_MAX = 180;

type Axis = "lat" | "lon";

/** Latin and Cyrillic both: the interface is Russian and so is the keyboard in front of it. */
const HEMISPHERES: Record<string, { axis: Axis; sign: 1 | -1 }> = {
  N: { axis: "lat", sign: 1 },
  S: { axis: "lat", sign: -1 },
  E: { axis: "lon", sign: 1 },
  W: { axis: "lon", sign: -1 },
  С: { axis: "lat", sign: 1 },
  Ю: { axis: "lat", sign: -1 },
  В: { axis: "lon", sign: 1 },
  З: { axis: "lon", sign: -1 },
};

/** Rank orders the DMS parts: a part can only follow one of a lower rank. */
const MARKER_RANK: Record<string, number> = {
  "°": 1,
  º: 1,
  "′": 2,
  "'": 2,
  "″": 3,
  '"': 3,
};

const NUMBER = /^[+-]?\d+(?:\.\d+)?/;
const MARKER = /^\s*(″|''|"|′|'|°|º)/;
const SEPARATOR = /[\s,;]/;

interface NumberToken {
  kind: "number";
  value: number;
  /** 0 = plain decimal, 1/2/3 = degrees/minutes/seconds. */
  rank: number;
}

interface HemisphereToken {
  kind: "hemisphere";
  axis: Axis;
  sign: 1 | -1;
}

type Token = NumberToken | HemisphereToken;

const scan = (text: string): Token[] | null => {
  const tokens: Token[] = [];
  let at = 0;
  while (at < text.length) {
    const char = text[at]!;
    if (SEPARATOR.test(char)) {
      at += 1;
      continue;
    }
    const hemisphere = HEMISPHERES[char.toUpperCase()];
    if (hemisphere) {
      tokens.push({ kind: "hemisphere", ...hemisphere });
      at += 1;
      continue;
    }
    const number = NUMBER.exec(text.slice(at));
    if (!number) return null;
    at += number[0].length;
    const marker = MARKER.exec(text.slice(at));
    let rank = 0;
    if (marker) {
      rank = marker[1] === "''" ? 3 : (MARKER_RANK[marker[1]!] ?? 0);
      at += marker[0].length;
    }
    tokens.push({ kind: "number", value: Number(number[0]), rank });
  }
  return tokens;
};

interface Group {
  parts: number[];
  lastRank: number;
  axis?: Axis;
  sign?: 1 | -1;
  /** No more numbers may join: seconds were seen, or a hemisphere letter closed it. */
  sealed: boolean;
}

const group = (tokens: Token[]): Group[] => {
  const groups: Group[] = [];
  const open = (axis?: Axis, sign?: 1 | -1): Group => {
    const item: Group = { parts: [], lastRank: 0, axis, sign, sealed: false };
    groups.push(item);
    return item;
  };
  const last = (): Group | undefined => groups[groups.length - 1];

  for (const token of tokens) {
    const current = last();
    if (token.kind === "hemisphere") {
      // A letter after a number names that number's axis; a letter before one opens the pair.
      if (current && current.parts.length > 0 && current.axis === undefined) {
        current.axis = token.axis;
        current.sign = token.sign;
        current.sealed = true;
      } else {
        open(token.axis, token.sign);
      }
      continue;
    }
    const joinable =
      current !== undefined &&
      !current.sealed &&
      current.parts.length < 3 &&
      (current.parts.length === 0 || token.rank > current.lastRank);
    const target = joinable ? current : open();
    target.parts.push(token.value);
    target.lastRank = token.rank;
    if (token.rank === 3) target.sealed = true;
  }
  return groups;
};

/** Degrees + minutes/60 + seconds/3600, with the sign of the letter beating the sign of the number. */
const degreesOf = (item: Group): { value: number } | { error: string } => {
  const [degrees = 0, minutes = 0, seconds = 0] = item.parts;
  if (item.parts.length > 1 && !Number.isInteger(degrees)) {
    return { error: T.coords.errors.unparsed };
  }
  if (item.parts.length > 2 && !Number.isInteger(minutes)) {
    return { error: T.coords.errors.unparsed };
  }
  if (minutes < 0 || minutes >= 60) return { error: T.coords.errors.minutes };
  if (seconds < 0 || seconds >= 60) return { error: T.coords.errors.seconds };
  const magnitude = Math.abs(degrees) + minutes / 60 + seconds / 3600;
  const sign = item.sign ?? (degrees < 0 ? -1 : 1);
  return { value: sign * magnitude };
};

/**
 * A pasted map link. Google's `/@lat,lng,zoom` and `?q=lat,lng` are latitude-first, which is
 * why they are the two forms read here. Yandex's `ll=` is the other way round and is left
 * alone on purpose: a silently transposed pair is worse than no support at all.
 */
const fromUrl = (text: string): string | null => {
  const at = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(text);
  if (at) return `${at[1]} ${at[2]}`;
  const query = /[?&](?:q|query|center|daddr|saddr)=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/.exec(text);
  if (query) return `${query[1]} ${query[2]}`;
  return null;
};

const looksLikeUrl = (text: string): boolean =>
  /^(https?:\/\/|www\.)/i.test(text) || text.includes("/maps");

/** `50,286 31,313` — a decimal comma, but only where it cannot be read as a separator. */
const DECIMAL_COMMA = /^([+-]?\d+),(\d+)\s+([+-]?\d+),(\d+)$/;

/** Copy-paste damage: unicode minus/dash for `-`, non-breaking and narrow spaces for ` `. */
const normalise = (input: string): string =>
  input.replace(/[\u2010-\u2015\u2212]/g, "-").replace(/[\u00a0\u202f\u2007\u2009]/g, " ");

export const parseCoordinates = (input: string): CoordParse => {
  const text = normalise(input).trim();
  if (text === "") return { ok: false, error: T.coords.errors.empty };

  let body = text;
  if (looksLikeUrl(text)) {
    const extracted = fromUrl(text);
    if (!extracted) return { ok: false, error: T.coords.errors.unparsed };
    body = extracted;
  }
  const decimalComma = DECIMAL_COMMA.exec(body);
  if (decimalComma)
    body = `${decimalComma[1]}.${decimalComma[2]} ${decimalComma[3]}.${decimalComma[4]}`;

  const tokens = scan(body);
  if (!tokens) return { ok: false, error: T.coords.errors.unparsed };

  const groups = group(tokens).filter((item) => item.parts.length > 0);
  if (groups.length !== 2) return { ok: false, error: T.coords.errors.needTwo };

  const [first, second] = groups as [Group, Group];
  if (first.axis && second.axis && first.axis === second.axis) {
    return { ok: false, error: T.coords.errors.sameAxis };
  }
  // Letters decide which is which; without them the reading is latitude first.
  const firstAxis: Axis = first.axis ?? (second.axis === "lat" ? "lon" : "lat");
  const secondAxis: Axis = firstAxis === "lat" ? "lon" : "lat";

  const firstValue = degreesOf(first);
  if ("error" in firstValue) return { ok: false, error: firstValue.error };
  const secondValue = degreesOf(second);
  if ("error" in secondValue) return { ok: false, error: secondValue.error };

  const lat = firstAxis === "lat" ? firstValue.value : secondValue.value;
  const lon = secondAxis === "lon" ? secondValue.value : firstValue.value;

  if (Math.abs(lat) > LAT_MAX) {
    // The near-miss worth naming: the pair is a valid point with the axes the other way round.
    // Only worth saying when the axes were NOT spelled out — with letters there is no order
    // left to guess at, and the number is simply wrong.
    const guessed = first.axis === undefined && second.axis === undefined;
    const transposed = guessed && Math.abs(lat) <= LON_MAX && Math.abs(lon) <= LAT_MAX;
    return { ok: false, error: transposed ? T.coords.errors.swapped : T.coords.errors.latRange };
  }
  if (Math.abs(lon) > LON_MAX) return { ok: false, error: T.coords.errors.lonRange };

  return { ok: true, point: { lat, lon } };
};

/** Read-back of an accepted point: the same digits the operator typed, rounded to ~1 m. */
export const formatPoint = (point: Point): string =>
  `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
